/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { createSlice } from '@reduxjs/toolkit';
import {
  AudioPropertiesInfo,
  LocalAudioStats,
  NetworkQuality,
  RemoteAudioStats,
} from '@volcengine/rtc';
import RtcClient from '@/lib/RtcClient';
import { getRouteInteractionMode } from '@/lib/interactionExperience';

const LEADING_CONTINUATION_PATTERN = /^[\s"”’')\]】》」』,，。！？!?；;：:、-]/;
const USER_SUBTITLE_NEW_TURN_GAP_MS = 1200;
const BRACKET_IMAGE_DIRECTIVE_PATTERN = /[（(]https?:\/\/[^）)\s]+[）)]/gi;
const MARKDOWN_IMAGE_DIRECTIVE_PATTERN = /!\[[^\]]*]\((https?:\/\/[^)\s]+)\)/gi;
const INLINE_URL_PATTERN = /https?:\/\/[^\s<>"')\]}]+[^\s<>"',.)\]}]/gi;

const shouldAppendContinuationSubtitle = (previousText = '', currentText = '') => {
  const prev = String(previousText || '').trim();
  const next = String(currentText || '').trim();
  if (!prev || !next) return false;
  if (LEADING_CONTINUATION_PATTERN.test(next)) return true;
  return /[“‘"(':：]$/.test(prev);
};

const shouldStartNewUserSubtitleTurn = (
  previousText = '',
  currentText = '',
  elapsedMs = 0
) => {
  const prev = String(previousText || '').trim();
  const next = String(currentText || '').trim();
  if (!prev || !next || prev === next) return false;
  if (next.startsWith(prev) || prev.startsWith(next)) return false;
  if (shouldAppendContinuationSubtitle(prev, next)) return false;
  return elapsedMs >= USER_SUBTITLE_NEW_TURN_GAP_MS;
};

const createMessageTimestamp = () => new Date().toISOString();

const extractImageDirectives = (text = '') => {
  const source = String(text || '');
  const directives = [
    ...Array.from(source.matchAll(BRACKET_IMAGE_DIRECTIVE_PATTERN), (match) => String(match[0] || '')),
    ...Array.from(source.matchAll(MARKDOWN_IMAGE_DIRECTIVE_PATTERN), (match) => String(match[0] || '')),
    ...Array.from(source.matchAll(INLINE_URL_PATTERN), (match) => String(match[0] || '')),
  ]
    .map((item) => item.trim())
    .filter(Boolean);
  return Array.from(new Set(directives));
};

const mergePreservedImageDirectives = (previousText = '', currentText = '') => {
  const previousDirectives = extractImageDirectives(previousText);
  if (!previousDirectives.length) return currentText;
  const currentDirectives = new Set(extractImageDirectives(currentText));
  if (currentDirectives.size) return currentText;
  const suffix = previousDirectives.filter((directive) => !String(currentText || '').includes(directive));
  if (!suffix.length) return currentText;
  return `${String(currentText || '').trim()}\n${suffix.join('\n')}`.trim();
};

export interface IUser {
  username?: string;
  userId?: string;
  /** Original RTC stream userId. Audio-volume callbacks are keyed by this id. */
  rtcUserId?: string;
  publishAudio?: boolean;
  publishVideo?: boolean;
  publishScreen?: boolean;
  audioStats?: RemoteAudioStats;
  audioPropertiesInfo?: AudioPropertiesInfo;
}

export type LocalUser = Omit<IUser, 'audioStats'> & {
  loginToken?: string;
  audioStats?: LocalAudioStats;
};

export interface Msg {
  value: string;
  time: string;
  user: string;
  paragraph?: boolean;
  definite?: boolean;
  isInterrupted?: boolean;
  images?: string[];
  source?: 'subtitle' | 'manual';
}

export type InteractionMode = 'desktop_companion' | 'smart_assistant' | 'immersive';

export interface SceneConfig {
  id: string;
  icon?: string;
  name?: string;
  userCallName?: string;
  personality?: string;
  voiceType?: string;
  questions?: string[];
  botName: string;
  isVision: boolean;
  isScreenMode: boolean;
  isInterruptMode: boolean;
  isAvatarScene: boolean;
  avatarBgUrl: string;
  /** Optional looping background video (mp4 / webm) for the active companion.
   *  Rendered behind the character on desktop-companion surfaces. */
  avatarBgVideoUrl?: string;
  /** Optional Live2D *.model3.json URL for the active companion. */
  live2dModelUrl?: string;
  /** Auto-generated paper-puppet frames (mouth-open / eyes-closed) for the
   *  active companion. When present, the portrait animates. */
  portraitFrames?: {
    mouthOpen?: string;
    eyesClosed?: string;
  };
  subtitleMode?: number;
}

export interface RTCConfig {
  AppId: string;
  RoomId: string;
  UserId: string;
  Token: string;
}

export interface RoomState {
  time: number;
  roomId?: string;
  localUser: LocalUser;
  remoteUsers: IUser[];
  autoPlayFailUser: string[];
  /**
   * @brief 是否已加房
   */
  isJoined: boolean;
  /**
   * @brief 选择的场景
   */
  scene: string;
  /**
   * @brief 场景下的配置
   */
  sceneConfigMap: Record<string, SceneConfig>;
  /**
   * @brief RTC 相关的配置
   */
  rtcConfigMap: Record<string, RTCConfig>;

  /**
   * @brief AI 通话是否启用
   */
  isAIGCEnable: boolean;
  /**
   * @brief AI 是否正在说话
   */
  isAITalking: boolean;
  /**
   * @brief AI 思考中
   */
  isAIThinking: boolean;
  /**
   * @brief 用户是否正在说话
   */
  isUserTalking: boolean;
  /**
   * @brief 网络质量
   */
  networkQuality: NetworkQuality;

  /**
   * @brief 对话记录
   */
  msgHistory: Msg[];

  /**
   * @brief 当前的对话
   */
  currentConversation: {
    [user: string]: {
      /**
       * @brief 实时对话内容
       */
      msg: string;
      /**
       * @brief 当前实时对话内容是否能被定义为 "问题"
       */
      definite: boolean;
    };
  };

  /**
   * @brief 是否显示字幕
   */
  isShowSubtitle: boolean;

  /**
   * @brief 是否全屏
   */
  isFullScreen: boolean;

  /**
   * @brief 自定义人设名称
   */
  customSceneName: string;
  /**
   * @brief 交互模式
   */
  interactionMode: InteractionMode;
}

const getInitialInteractionMode = (): InteractionMode => {
  if (typeof window === 'undefined') return 'smart_assistant';
  return getRouteInteractionMode(window.location.pathname);
};

const initialState: RoomState = {
  time: -1,
  scene: '',
  sceneConfigMap: {},
  rtcConfigMap: {},
  remoteUsers: [],
  localUser: {
    publishAudio: false,
    publishVideo: false,
    publishScreen: false,
  },
  autoPlayFailUser: [],
  isJoined: false,
  isAIGCEnable: false,
  isAIThinking: false,
  isAITalking: false,
  isUserTalking: false,
  networkQuality: NetworkQuality.UNKNOWN,

  msgHistory: [],
  currentConversation: {},
  isShowSubtitle: true,
  isFullScreen: false,
  customSceneName: '',
  interactionMode: getInitialInteractionMode(),
};

export const roomSlice = createSlice({
  name: 'room',
  initialState,
  reducers: {
    localJoinRoom: (
      state,
      {
        payload,
      }: {
        payload: {
          roomId: string;
          user: LocalUser;
        };
      }
    ) => {
      state.roomId = payload.roomId;
      state.localUser = {
        ...state.localUser,
        ...payload.user,
      };
      state.isJoined = true;
    },
    localLeaveRoom: (state) => {
      state.roomId = undefined;
      state.time = -1;
      state.localUser = {
        publishAudio: false,
        publishVideo: false,
        publishScreen: false,
      };
      state.remoteUsers = [];
      state.isJoined = false;
    },
    remoteUserJoin: (state, { payload }) => {
      const findIndex = state.remoteUsers.findIndex(
        (user) =>
          user.userId === payload.userId ||
          user.rtcUserId === payload.userId ||
          (payload.rtcUserId &&
            (user.userId === payload.rtcUserId || user.rtcUserId === payload.rtcUserId))
      );
      if (findIndex < 0) {
        state.remoteUsers.push(payload);
      } else {
        state.remoteUsers[findIndex] = {
          ...state.remoteUsers[findIndex],
          ...payload,
        };
      }
    },
    remoteUserLeave: (state, { payload }) => {
      const findIndex = state.remoteUsers.findIndex(
        (user) => user.userId === payload.userId || user.rtcUserId === payload.userId
      );
      if (findIndex >= 0) {
        state.remoteUsers.splice(findIndex, 1);
      }
    },

    updateScene: (state, { payload }) => {
      state.scene = payload;
    },

    updateSceneConfig: (state, { payload }) => {
      state.sceneConfigMap = payload;
    },

    updateRTCConfig: (state, { payload }) => {
      state.rtcConfigMap = payload;
      RtcClient.basicInfo = {
        app_id: payload[state.scene].AppId,
        room_id: payload[state.scene].RoomId,
        user_id: payload[state.scene].UserId,
        token: payload[state.scene].Token,
      };
    },

    updateLocalUser: (state, { payload }: { payload: Partial<LocalUser> }) => {
      state.localUser = {
        ...state.localUser,
        ...(payload || {}),
      };
    },

    updateNetworkQuality: (state, { payload }) => {
      state.networkQuality = payload.networkQuality;
    },

    updateRemoteUser: (state, { payload }: { payload: IUser | IUser[] }) => {
      if (!Array.isArray(payload)) {
        payload = [payload];
      }

      payload.forEach((user) => {
        const findIndex = state.remoteUsers.findIndex(
          (u) =>
            u.userId === user.userId ||
            u.rtcUserId === user.userId ||
            (user.rtcUserId && (u.userId === user.rtcUserId || u.rtcUserId === user.rtcUserId))
        );
        if (findIndex < 0) {
          state.remoteUsers.push(user);
        } else {
          state.remoteUsers[findIndex] = {
            ...state.remoteUsers[findIndex],
            ...user,
          };
        }
      });
    },

    updateRoomTime: (state, { payload }) => {
      state.time = payload.time;
    },

    addAutoPlayFail: (state, { payload }) => {
      const autoPlayFailUser = state.autoPlayFailUser;
      const index = autoPlayFailUser.findIndex((item) => item === payload.userId);
      if (index === -1) {
        state.autoPlayFailUser.push(payload.userId);
      }
    },
    removeAutoPlayFail: (state, { payload }) => {
      const autoPlayFailUser = state.autoPlayFailUser;
      const _autoPlayFailUser = autoPlayFailUser.filter((item) => item !== payload.userId);
      state.autoPlayFailUser = _autoPlayFailUser;
    },
    clearAutoPlayFail: (state) => {
      state.autoPlayFailUser = [];
    },
    updateAIGCState: (state, { payload }) => {
      state.isAIGCEnable = payload.isAIGCEnable;
    },
    updateAITalkState: (state, { payload }) => {
      state.isAIThinking = false;
      state.isUserTalking = false;
      state.isAITalking = payload.isAITalking;
    },
    updateAIThinkState: (state, { payload }) => {
      state.isAIThinking = payload.isAIThinking;
      state.isUserTalking = false;
    },
    clearHistoryMsg: (state) => {
      state.msgHistory = [];
    },
    setHistoryMsg: (state, { payload }) => {
      const { paragraph, definite } = payload;
      const lastMsg = state.msgHistory.at(-1)! || {};
      if (
        lastMsg?.user === payload.user &&
        lastMsg?.value === payload.text &&
        paragraph &&
        definite &&
        !payload.images?.length
      ) {
        return;
      }
      /** 是否需要再创建新句子 */
      const fromBot =
        payload.user === state.sceneConfigMap[state.scene].botName ||
        payload.user.includes('voiceChat_');
      /**
       * Bot 的语句：
       * 1. 在 SubtitleMode=0 时（未启用数字人时默认值），以 definite 判断是否需要追加新内容
       * 2. 在 SubtitleMode=1 时（启用数字人时强制设定为 1），以 paragraph 判断是否需要追加新内容
       * User 的语句以 paragraph 判断是否需要追加新内容
       */
      const currentSubtitleMode =
        state.sceneConfigMap[state.scene].subtitleMode ??
        (state.sceneConfigMap[state.scene].isAvatarScene ? 1 : 0);
      const lastMsgCompleted = fromBot
        ? currentSubtitleMode
          ? Boolean(lastMsg.definite || lastMsg.paragraph)
          : Boolean(lastMsg.definite)
        : Boolean(lastMsg.paragraph);
      const speakerChanged = lastMsg.user && lastMsg.user !== payload.user;
      const lastMsgTs = new Date(lastMsg.time || '').getTime();
      const elapsedSinceLastMsg = Number.isFinite(lastMsgTs) ? Date.now() - lastMsgTs : 0;
      const forceNewUserTurn =
        !fromBot &&
        !speakerChanged &&
        !lastMsgCompleted &&
        shouldStartNewUserSubtitleTurn(lastMsg.value, payload.text, elapsedSinceLastMsg);

      if (state.msgHistory.length) {
        /** 如果上一句话是完整的则新增语句 */
        if (
          !speakerChanged &&
          fromBot &&
          lastMsgCompleted &&
          shouldAppendContinuationSubtitle(lastMsg.value, payload.text)
        ) {
          lastMsg.value = `${lastMsg.value}${payload.text}`;
          lastMsg.time = createMessageTimestamp();
          lastMsg.paragraph = paragraph;
          lastMsg.definite = definite;
          lastMsg.user = payload.user;
          lastMsg.source = 'subtitle';
        } else if (lastMsgCompleted || speakerChanged || forceNewUserTurn) {
          state.msgHistory.push({
            value: payload.text,
            time: createMessageTimestamp(),
            user: payload.user,
            definite,
            paragraph,
            source: 'subtitle',
          });
        } else {
          /** 话未说完, 更新文字内容 */
          lastMsg.value = fromBot
            ? mergePreservedImageDirectives(lastMsg.value, payload.text)
            : payload.text;
          lastMsg.time = createMessageTimestamp();
          lastMsg.paragraph = paragraph;
          lastMsg.definite = definite;
          lastMsg.user = payload.user;
          lastMsg.source = 'subtitle';
        }
      } else {
        /** 首句话首字不会被打断 */
        state.msgHistory.push({
          value: payload.text,
          time: createMessageTimestamp(),
          user: payload.user,
          paragraph,
          source: 'subtitle',
        });
      }
    },
    setInterruptMsg: (state) => {
      state.isAITalking = false;
      if (!state.msgHistory.length) {
        return;
      }
      /** 找到最后一个末尾的字幕, 将其状态置换为打断 */
      for (let id = state.msgHistory.length - 1; id >= 0; id--) {
        const msg = state.msgHistory[id];
        if (msg.value) {
          if (!msg.definite) {
            state.msgHistory[id].isInterrupted = true;
          }
          break;
        }
      }
    },
    clearCurrentMsg: (state) => {
      state.currentConversation = {};
      state.msgHistory = [];
      state.isAITalking = false;
      state.isUserTalking = false;
    },
    updateShowSubtitle: (state, { payload }) => {
      state.isShowSubtitle = payload.isShowSubtitle;
    },
    updateFullScreen: (state, { payload }) => {
      state.isFullScreen = payload.isFullScreen;
    },
    updatecustomSceneName: (state, { payload }) => {
      state.customSceneName = payload.customSceneName;
    },
    appendLocalMsg: (state, { payload }: { payload: Msg }) => {
      state.msgHistory.push({
        ...payload,
        time: payload.time || createMessageTimestamp(),
      });
    },
    updateInteractionMode: (
      state,
      { payload }: { payload: { interactionMode: InteractionMode; persist?: boolean } }
    ) => {
      state.interactionMode = payload.interactionMode;
      if (payload.persist !== false && typeof window !== 'undefined') {
        localStorage.setItem('interaction_mode', payload.interactionMode);
      }
    },
  },
});

export const {
  localJoinRoom,
  localLeaveRoom,
  remoteUserJoin,
  remoteUserLeave,
  updateRemoteUser,
  updateLocalUser,
  updateRoomTime,
  addAutoPlayFail,
  removeAutoPlayFail,
  clearAutoPlayFail,
  updateAIGCState,
  updateAITalkState,
  updateAIThinkState,
  setHistoryMsg,
  clearHistoryMsg,
  clearCurrentMsg,
  setInterruptMsg,
  updateNetworkQuality,
  updateScene,
  updateSceneConfig,
  updateRTCConfig,
  updateShowSubtitle,
  updateFullScreen,
  updatecustomSceneName,
  appendLocalMsg,
  updateInteractionMode,
} = roomSlice.actions;

export default roomSlice.reducer;
